import { type Refusal, fieldLabel, toRefusal } from '../refusals';

/**
 * The tenant administration's refusal codes in plain language
 * (tenant-admin-api.md, the tables of each route). The UI shows its own text
 * per code and never the server's message; `extra` is read only for what the
 * text needs (a domain the caller typed, a seat count). Minimal disclosure:
 * no permission names, no other organisation, no scope lists.
 *
 * Codes the access administration already knows keep their text from
 * ../refusals.ts unless the tenant meaning differs (listed here).
 */
const TEXT: Record<string, (extra: Record<string, unknown>) => string> = {
  // who may act
  permission_required: () => 'Your active role cannot do this.',
  organisation_admin_role_required: () => 'Only an administrator of the organisation can do this.',
  organisation_admin_required: () => 'Only an administrator of the organisation can do this.',
  internal_approver_required: () => 'This step is decided by a Super Admin.',
  self_approval_not_allowed: () =>
    'You cannot approve your own request, or a request for yourself. Another person has to decide it.',
  self_administration_not_allowed: () =>
    'You cannot do this for yourself (invite, change, renew or cancel on your own behalf). Another person has to.',
  organisation_managed_by_asi: () =>
    'PartnershipNet manages access for this organisation. Please ask PartnershipNet to add the person.',
  admin_role_requires_approval: () =>
    'The administrator role is not given or changed here: making, changing or removing an administrator needs a second person.',
  // not found (the same answer for unknown and not visible)
  organisation_not_found: () => 'This organisation was not found.',
  request_not_found: () => 'This request was not found (any more).',
  membership_not_found: () => 'This membership was not found (any more).',
  change_not_found: () => 'This change was not found (any more).',
  // set-up changes
  nothing_to_change: () => 'Nothing differs from the current set-up.',
  package_not_allowed_for_type: () =>
    'A chosen package is not available for this organisation type.',
  domain_invalid: (e) => `${quoted(e.domain)}is not a valid domain.`,
  domain_public: (e) =>
    `${quoted(e.domain)}is a public e-mail domain and cannot belong to an organisation.`,
  domain_internal: (e) => `${quoted(e.domain)}belongs to PartnershipNet and cannot be registered.`,
  domain_taken: (e) => `${quoted(e.domain)}already belongs to another organisation.`,
  managed_by_needs_admin: () =>
    'An organisation that manages its own access needs an administrator. It has no active one: name at least one in this change.',
  change_pending: () =>
    'Another change of the same kind is still waiting for a decision. Decide or cancel it first.',
  reason_required: () => 'Please give a reason.',
  change_not_pending: () => 'This change has already been decided.',
  change_expired: () => 'This change waited 14 days without a decision and has expired.',
  // invitations and memberships
  seat_limit_reached: () =>
    'The organisation has no free seat. Free a seat or ask PartnershipNet to raise the limit.',
  role_not_assignable: () => 'This role cannot be given in this organisation.',
  role_outside_packages: () =>
    'This role holds features the organisation’s packages do not include.',
  permission_not_delegable: () =>
    'This role holds rights an organisation cannot give. Choose another role.',
  duration_too_long: (e) =>
    `A membership can last at most ${typeof e.max_months === 'number' ? e.max_months : 12} months.`,
  end_date_in_past: () => 'The end date cannot be in the past.',
  rules_required: () => 'Choose the access (brands) for an agency membership.',
  membership_exists: () => 'This person already has this role here, or an open request for it.',
  membership_not_running: () => 'This membership is not running (any more).',
  review_not_open: () => 'No review of this membership waits for a confirmation (any more).',
  external_cannot_be_admin: (e) =>
    `${who(e.email)}A person from another organisation cannot be an administrator here.`,
  external_needs_home_account: () =>
    'This address belongs to another organisation that is a client: that organisation adds the person first, then you can invite them.',
  internal_guest_status_not_allowed: (e) =>
    `${who(e.email)}PartnershipNet staff can be guests only of a client, trial, demo or test organisation.`,
  internal_guest_self_managed: (e) =>
    `${who(e.email)}This organisation manages its own access, so PartnershipNet staff cannot be added as guests.`,
  internal_guest_renew_not_allowed: () =>
    'This organisation manages its own access, so PartnershipNet cannot renew this guest. Only its administrators can.',
  internal_person_cannot_be_admin: (e) =>
    `${who(e.email)}PartnershipNet staff cannot be an organisation’s administrator.`,
  internal_person_needs_account: () =>
    'This PartnershipNet address has no account yet. Add the person as staff first, then invite them.',
  // the code of the earlier test-only rule: a failed set-up change may still show it
  internal_person_test_only: (e) => `${who(e.email)}PartnershipNet staff cannot be added here.`,
  request_not_pending: () => 'This request has already been decided.',
  request_expired: () => 'This request waited 14 days without a decision and has expired.',
  change_not_allowed_at_step: () =>
    'At this step the role, access and end date cannot be changed. The organisation’s administrator can change them.',
  last_admin: () =>
    'This is the organisation’s only administrator. Add another administrator first.',
  // the invited person
  invitation_not_valid: () =>
    'This invitation link can no longer be used. It may have expired, been used or been withdrawn. Please ask for a new invitation.',
  invitation_other_address: () =>
    'You are signed in with another e-mail address. Sign out, then sign in with the address the invitation was sent to.',
  invitation_provider_required: () =>
    'Please sign in with your company’s single sign-on (SSO) to accept this invitation.',
  account_exists: () => 'An account for this address exists now. Please sign in.',
  link_not_valid: () => 'This link can no longer be used. Please request access again.',
  // the service is not there yet
  tenant_administration_unavailable: () => 'This function is not available yet.',
  network: () => 'The service did not answer. Please try again.',
};

/** "a@b.com: " for a refusal that names the address the caller typed. */
const who = (v: unknown): string =>
  typeof v === 'string' && v.trim() !== '' ? `${v.trim()}: ` : '';

const quoted = (v: unknown): string =>
  typeof v === 'string' && v.trim() !== '' ? `“${v.trim()}” ` : 'This domain ';

/** Plain text of a tenant refusal code; null when the code is not a tenant code. */
export function tenantReasonText(code: string, extra: Record<string, unknown> = {}): string | null {
  const fn = TEXT[code];
  return fn ? fn(extra) : null;
}

/** A refusal from an identity answer with the tenant texts; null on success. */
export function tenantRefusal(payload: unknown): Refusal | null {
  const r = toRefusal(payload);
  if (!r) return null;
  if (r.reason) {
    const text = tenantReasonText(r.reason, r.extra);
    return text ? { ...r, message: text } : r;
  }
  // 422 with fields: a field's message may itself be a tenant code
  const fields: Record<string, string> = {};
  const raw = (payload as { message?: unknown }).message as Record<string, unknown>;
  for (const [field, text] of Object.entries(r.fields)) {
    const codes = Array.isArray(raw?.[field]) ? (raw[field] as unknown[]) : [raw?.[field]];
    const own = codes
      .map((c) => tenantReasonText(String(c), r.extra))
      .filter((t): t is string => t !== null);
    fields[field] = own.length > 0 ? own.join(' ') : text;
  }
  const message = Object.entries(fields)
    .map(([field, text]) => `${fieldLabel(field)}: ${text}`)
    .join(' ');
  return { ...r, fields, message };
}

/** The text of one error of POST invitations/check (`{code, ...extra}`). */
export function checkErrorText(issue: { code: string; [k: string]: unknown }): string {
  const { code, ...extra } = issue;
  if (code === 'invalid_input') {
    const field = typeof extra.field === 'string' ? extra.field : '';
    return field ? `${fieldLabel(field)}: please check this field.` : 'Please check the form.';
  }
  // the issue's details (max_months, client_status, ...) go with the code
  return tenantReasonText(code, extra) ?? toRefusal({ ...extra, message: code })!.message;
}

/**
 * The errors of POST invitations/check that are about the address itself (who the person
 * is, where their home is), shown under the e-mail field as soon as it is typed.
 */
const EMAIL_ISSUES = new Set([
  'external_needs_home_account',
  'internal_guest_status_not_allowed',
  'internal_guest_self_managed',
  'internal_person_cannot_be_admin',
  'external_cannot_be_admin',
  'self_administration_not_allowed',
  'membership_exists',
  'use_invitation',
]);

export const isEmailIssue = (issue: { code: string; [k: string]: unknown }): boolean =>
  EMAIL_ISSUES.has(issue.code) || (issue.code === 'invalid_input' && issue.field === 'email');

/** The text of one warning of POST invitations/check. */
export function checkWarningText(issue: { code: string }): string {
  if (issue.code === 'normal_account')
    return 'The address is on a public or unregistered e-mail domain: the person gets a normal account with an end date.';
  return issue.code;
}

/**
 * Is the answer "this route or table does not exist here"? (the server is
 * older than the tenant administration, or its database update has not run).
 * Then the new parts stay hidden.
 */
export function isUnavailable(r: Refusal): boolean {
  return (
    r.reason === 'tenant_administration_unavailable' ||
    r.reason === 'identity_api_not_configured' ||
    r.reason === 'network' ||
    // nothing answered (network error, identity not reachable)
    r.status === 0 ||
    (r.status === 404 && r.reason === 'Not Found') ||
    (r.status === 405 && r.reason === 'Method Not Allowed')
  );
}
